import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Sparkles, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { SiteLayout, useSettings, waLink } from "@/components/SiteLayout";
import { useI18n } from "@/lib/i18n";
import { api, rp, BACKEND } from "@/lib/api";
import { logError } from "@/lib/log";
import { SITE_MEDIA_DEFAULTS } from "@/content/siteMediaDefaults";

const HERO_FALLBACK = SITE_MEDIA_DEFAULTS.hero;
const TILES_FALLBACK = SITE_MEDIA_DEFAULTS.tiles;

const daysLeftLabel = (endDate, t) => {
  if (!endDate) return null;
  const end = new Date(`${endDate.slice(0, 10)}T23:59:59`);
  const days = Math.ceil((end - new Date()) / 86400000);
  if (days < 0) return null;
  if (days === 0) return t("promoLastDay");
  if (days === 1) return t("promoTomorrow");
  return t("promoInDays").replace("{n}", days);
};

export const BannerSlider = ({ banners }) => {
  const { t, lang } = useI18n();
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const drag = useRef({ x: 0, active: false });
  const n = banners.length;

  const go = (next) => setI(((next % n) + n) % n);
  useEffect(() => {
    if (n < 2 || paused) return;
    const t = setInterval(() => setI((x) => (x + 1) % n), 5000);
    return () => clearInterval(t);
  }, [n, paused]);
  if (!n) return null;

  const onDown = (x) => { drag.current = { x, active: true }; setPaused(true); };
  const onUp = (x) => {
    if (!drag.current.active) return;
    const dx = x - drag.current.x;
    drag.current.active = false;
    setPaused(false);
    if (Math.abs(dx) > 45) go(dx < 0 ? i + 1 : i - 1);
  };

  return (
    <section className="mx-auto max-w-6xl px-5 pt-8" data-testid="banner-slider">
      <div className="relative overflow-hidden rounded-2xl aspect-[21/9] bg-cream-dark select-none cursor-grab active:cursor-grabbing"
        onMouseEnter={() => setPaused(true)} onMouseLeave={() => { setPaused(false); drag.current.active = false; }}
        onMouseDown={(e) => onDown(e.clientX)} onMouseUp={(e) => onUp(e.clientX)}
        onTouchStart={(e) => onDown(e.touches[0].clientX)}
        onTouchEnd={(e) => onUp(e.changedTouches[0].clientX)}>
        <div className="flex h-full transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
          style={{ transform: `translateX(-${i * 100}%)` }} data-testid="banner-track">
          {banners.map((b) => {
            const countdown = daysLeftLabel(b.end_date, t);
            const title = (lang === "en" && b.title_en) || b.title;
            const subtitle = (lang === "en" && b.subtitle_en) || b.subtitle;
            const inner = (
              <>
                <img src={b.url} alt={title || "Promo"} draggable="false"
                  className="w-full h-full object-cover pointer-events-none" />
                {countdown && (
                  <div data-testid={`banner-countdown-${b.id}`}
                    className="absolute top-4 left-4 md:top-6 md:left-6 inline-flex items-center gap-1.5 rounded-full bg-lm-red/95 text-cream text-[11px] md:text-xs font-semibold px-3 py-1.5 backdrop-blur shadow-lg">
                    <Clock size={13} /> {countdown}
                  </div>
                )}
                {(title || subtitle) && (
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent flex items-end p-6 md:p-10">
                    <div>
                      {title && <div className="font-display text-2xl md:text-4xl text-cream tracking-tight">{title}</div>}
                      {subtitle && <div className="mt-1 text-sm md:text-base text-cream/80">{subtitle}</div>}
                    </div>
                  </div>
                )}
              </>
            );
            return (
              <div key={b.id} className="relative min-w-full h-full" data-testid={`banner-slide-${b.id}`}>
                {b.link_url ? <a href={b.link_url} className="block w-full h-full">{inner}</a> : inner}
              </div>
            );
          })}
        </div>

        {n > 1 && (
          <>
            <button data-testid="banner-prev" aria-label={t("bannerPrev")} onClick={() => go(i - 1)}
              className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-ink/50 text-cream backdrop-blur grid place-items-center hover:bg-ink/80 transition-colors">
              <ChevronLeft size={18} />
            </button>
            <button data-testid="banner-next" aria-label={t("bannerNext")} onClick={() => go(i + 1)}
              className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-ink/50 text-cream backdrop-blur grid place-items-center hover:bg-ink/80 transition-colors">
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-3 right-4 flex gap-2">
              {banners.map((x, idx) => (
                <button key={x.id} data-testid={`banner-dot-${idx}`} onClick={() => go(idx)}
                  aria-label={`Banner ${idx + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${idx === i ? "w-6 bg-cream" : "w-2 bg-cream/50 hover:bg-cream/80"}`} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export const PackageCard = ({ p, onSelect, selected, ctaLabel, disabled, disabledLabel }) => (
  <div data-testid={`package-card-${p.id}`}
    className={`lm-card rounded-xl p-6 flex flex-col ${selected ? "border-lm-red ring-1 ring-lm-red" : ""}`}>
    {p.badge ? (
      <span className="self-start mb-3 text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full bg-lm-gold/15 text-lm-gold border border-lm-gold/30">{p.badge}</span>
    ) : <span className="mb-3 h-[22px]" />}
    <h3 className="font-display text-2xl tracking-tight">{p.name}</h3>
    <p className="text-sm text-ink-soft mt-1" data-testid={`package-duration-${p.id}`}>
      {p.duration_display
        || `${p.duration_value} ${p.duration_type === "hour" ? "hour(s)" : p.duration_type === "day" ? "day(s)" : "custom"}`}
    </p>
    <div className="mt-4">
      {p.promo_active ? (
        <div className="flex items-baseline gap-2">
          <span className="text-ink-soft line-through text-sm" data-testid={`package-reference-${p.id}`}>{rp(p.normal_price)}</span>
          <span className="text-2xl font-semibold text-lm-red" data-testid={`package-price-${p.id}`}>{rp(p.price_used)}</span>
        </div>
      ) : (
        <span className="text-2xl font-semibold" data-testid={`package-price-${p.id}`}>{p.normal_price > 0 ? rp(p.price_used ?? p.normal_price) : "Custom Quote"}</span>
      )}
    </div>
    {p.duration_type === "weekday_block" && (
      <p className="mt-2 text-[11px] font-semibold uppercase tracking-widest text-lm-red"
        data-testid={`package-monday-only-${p.id}`}>Mulai hari Senin saja</p>
    )}
    {p.description && <p className="text-sm text-ink-soft mt-3 flex-1">{p.description}</p>}
    {onSelect ? (
      <button data-testid={`select-package-${p.id}`} onClick={() => onSelect(p)} disabled={!!disabled}
        aria-disabled={!!disabled}
        className={`mt-5 w-full rounded-full py-3 text-sm font-semibold ${disabled ? "bg-black/10 text-ink-soft cursor-not-allowed" : selected ? "bg-ink text-cream" : "lm-btn-primary"}`}>
        {disabled ? (disabledLabel || "Tidak tersedia") : selected ? ctaLabel?.selected : ctaLabel?.select}
      </button>
    ) : (
      <Link to="/book" className="mt-5 w-full text-center rounded-full py-3 text-sm font-semibold lm-btn-primary">{ctaLabel?.select}</Link>
    )}
  </div>
);

export const PhotoGallery = ({ photos }) => {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(null);
  if (!photos.length) return null;
  const cap = (p) => (lang === "en" && p.title_en) || p.title;
  return (
    <section className="mx-auto max-w-6xl px-5 py-20" data-testid="photo-gallery">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight">{t("galleryTitle")}</h2>
          <p className="mt-2 text-sm text-ink-soft max-w-lg">{t("gallerySub")}</p>
        </div>
        <Link to="/book" className="text-sm text-lm-red hover:underline" data-testid="gallery-book-link">
          {t("bookNow")} →
        </Link>
      </div>
      <div className="mt-10 grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {photos.map((p) => (
          <button key={p.id} data-testid={`gallery-item-${p.id}`} onClick={() => setOpen(p)}
            className="group relative overflow-hidden rounded-xl bg-cream-dark aspect-[4/3]">
            <img src={p.url} alt={cap(p) || t("galleryAlt")} loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
            {cap(p) && (
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/85 to-transparent text-cream text-xs p-3 text-left opacity-0 group-hover:opacity-100 transition-opacity">
                {cap(p)}
              </span>
            )}
          </button>
        ))}
      </div>

      {open && (
        <div data-testid="gallery-lightbox" onClick={() => setOpen(null)}
          className="fixed inset-0 z-[70] bg-ink/90 backdrop-blur-sm grid place-items-center p-5">
          <figure className="max-w-4xl">
            <img src={open.url} alt={cap(open) || t("galleryAlt")} className="w-full max-h-[80vh] object-contain rounded-xl" />
            {cap(open) && (
              <figcaption className="mt-3 text-center text-cream/80 text-sm">{cap(open)}</figcaption>
            )}
          </figure>
          <button data-testid="gallery-close" onClick={(e) => { e.stopPropagation(); setOpen(null); }}
            className="absolute top-5 right-6 text-cream/70 text-sm hover:text-cream">{t("close")} ✕</button>
        </div>
      )}
    </section>
  );
};

export default function Landing() {
  const { t } = useI18n();
  const s = useSettings();
  const [pkgs, setPkgs] = useState([]);
  const [media, setMedia] = useState([]);
  const bookNowCta = useMemo(() => ({ select: t("bookNow") }), [t]);
  useEffect(() => {
    api.get("/public/packages").then((r) => setPkgs(r.data)).catch((e) => logError("Failed to load packages", e));
    api.get("/public/site-media").then((r) => setMedia(r.data)).catch(() => setMedia([]));
  }, []);
  const pick = (slot) => media.find((m) => m.slot === slot);
  const banners = media.filter((m) => m.slot === "banner")
    .map((m) => ({ ...m, url: `${BACKEND}${m.url}` }));
  const hero = pick("hero") ? `${BACKEND}${pick("hero").url}` : HERO_FALLBACK;
  const tiles = pick("tiles") ? `${BACKEND}${pick("tiles").url}` : TILES_FALLBACK;
  const gallery = media.filter((m) => m.slot === "gallery").map((m) => ({ ...m, url: `${BACKEND}${m.url}` }));

  return (
    <SiteLayout>
      <section id="homepage-hero" className="relative">
        <div className="absolute inset-0">
          <img src={hero} alt="Friends playing mahjong" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-ink/65" />
        </div>
        <div className="relative mx-auto max-w-6xl px-5 py-28 md:py-40">
          <div className="max-w-2xl lm-fade">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-lm-gold">
              <Sparkles size={14} /> Jakarta · Portable Automatic Mahjong
            </span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-cream leading-[1.05]">
              {t("heroTitle")}
            </h1>
            <p className="mt-5 text-base md:text-lg text-cream/80 max-w-xl">{t("heroSub")}</p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link
                to="/book"
                data-testid="hero-book-btn"
                className={[
                  "lm-btn-primary inline-flex w-full items-center justify-center rounded-full",
                  "px-8 py-4 text-sm font-semibold sm:w-auto",
                ].join(" ")}
              >
                {t("bookNow")}
              </Link>
              <a
                href={waLink(
                  s?.whatsapp_number,
                  "Hello LapakMahjong.id, I would like to discuss an event or collaboration.",
                )}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-rent-collaboration-whatsapp-btn"
                className={[
                  "inline-flex w-full items-center justify-center gap-2 rounded-full bg-cream px-8",
                  "py-4 text-sm font-semibold text-ink transition-colors hover:bg-white sm:w-auto",
                ].join(" ")}
              >
                <MessageCircle size={16} />
                {t("heroRentCollaboration")}
              </a>
            </div>
          </div>
        </div>
      </section>

      <div id="homepage-banners"><BannerSlider banners={banners} /></div>

      <section id="homepage-how-it-works" className="mx-auto max-w-6xl px-5 py-20 md:py-28">
        <div className="grid md:grid-cols-[1fr_1.2fr] gap-12 items-start">
          <div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight">{t("howItWorks")}</h2>
            <img src={tiles} alt="Mahjong tiles" className="mt-8 rounded-xl w-full h-64 object-cover hidden md:block" />
          </div>
          <ol className="space-y-5">
            {t("steps").map((step, i) => (
              <li key={step} data-testid={`how-step-${i + 1}`} className="lm-fade flex gap-4 items-start"
                style={{ animationDelay: `${i * 70}ms` }}>
                <span className="shrink-0 w-9 h-9 rounded-full grid place-items-center border border-lm-red/30 text-lm-red text-sm font-semibold">{i + 1}</span>
                <span className="pt-1.5 text-base">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-cream-dark py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <h2 className="font-display text-3xl md:text-4xl tracking-tight">{t("packages")}</h2>
            <Link to="/book" className="text-sm text-lm-red hover:underline" data-testid="packages-book-link">{t("bookNow")} →</Link>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {pkgs.map((p) => <PackageCard key={p.id} p={p} ctaLabel={bookNowCta} />)}
          </div>
        </div>
      </section>

      <div id="homepage-gallery"><PhotoGallery photos={gallery} /></div>

      <section className="mx-auto max-w-6xl px-5 py-20">
        <div className="rounded-2xl bg-ink text-cream px-8 py-14 md:px-14 flex flex-col md:flex-row md:items-center gap-8 justify-between">
          <div>
            <h2 className="font-display text-3xl tracking-tight">{t("readyTitle")}</h2>
            <p className="mt-2 text-cream/70 text-sm">{t("readySub")}</p>
          </div>
          <Link to="/book" data-testid="cta-book-btn" className="lm-btn-primary rounded-full px-8 py-4 text-sm font-semibold self-start">
            {t("bookMahjong")}
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
